import {
  AssignableVehicle,
  AssignableVehicleEligibilityResponse,
  AssignableVehicleResponse,
  VehicleAmenityDTO,
  VehicleType,
} from '@/models/dto'
import {
  VehicleAmenityAttestedStatus,
  VehicleAmenityReviewStatus,
  VehicleEligibilityStatus,
  VehicleTypeId,
  VehicleTypeKey,
  VehicleTypeLabel,
} from './enum'

/**
 * Mirrors VehicleDetail "Features" tab warning: incomplete features, or an amenity
 * under review that is waiting on operator evidence.
 */
export function shouldShowVehicleFeaturesAlert(
  isFeaturesComplete: boolean,
  vehicleAmenityDTOs: VehicleAmenityDTO[] | undefined
): boolean {
  const dtos = vehicleAmenityDTOs ?? []
  return (
    !isFeaturesComplete ||
    dtos.some(
      (va) =>
        va.attestedStatus === VehicleAmenityAttestedStatus.UnderReview &&
        va.review?.status === VehicleAmenityReviewStatus.AwaitingEvidence
    )
  )
}

/**
 * Applies the Features-tab Yes/No toggle for one amenity (see VehicleDetailFeatures).
 * `lastSavedAmenities` is the API snapshot used to restore properties and UnderReview.
 */
export function applyAmenityMeetsExpectationToggle(
  amenities: VehicleAmenityDTO[],
  amenityId: number,
  meetsExpectation: boolean,
  lastSavedAmenities: VehicleAmenityDTO[]
): VehicleAmenityDTO[] {
  return amenities.map((amenity) => {
    if (amenity.amenityId !== amenityId) {
      return amenity
    }

    if (!meetsExpectation) {
      return {
        ...amenity,
        attestedStatus: VehicleAmenityAttestedStatus.DoesNotMeetExpectation,
        properties: {},
      }
    }

    const original = lastSavedAmenities.find((va) => va.amenityId === amenityId)
    const wasUnderReview =
      original?.attestedStatus === VehicleAmenityAttestedStatus.UnderReview

    const attestedStatus = wasUnderReview
      ? VehicleAmenityAttestedStatus.UnderReview
      : VehicleAmenityAttestedStatus.MeetsExpectation

    return {
      ...amenity,
      attestedStatus,
      properties: original?.properties ?? {},
    }
  })
}

export const VEHICLE_TYPES: VehicleType[] = [
  {
    id: VehicleTypeId.CharterBus,
    key: VehicleTypeKey.CharterBus,
    label: 'Charter Bus',
  },
  {
    id: VehicleTypeId.MiniBus,
    key: VehicleTypeKey.MiniBus,
    label: 'Mini Bus',
  },
  {
    id: VehicleTypeId.Sprinter,
    key: VehicleTypeKey.Sprinter,
    label: 'Sprinter',
  },
  {
    id: VehicleTypeId.PartyBus,
    key: VehicleTypeKey.PartyBus,
    label: 'Party Bus',
  },
  {
    id: VehicleTypeId.Sedan,
    key: VehicleTypeKey.Sedan,
    label: 'Sedan',
  },
  {
    id: VehicleTypeId.SUV,
    key: VehicleTypeKey.SUV,
    label: 'SUV',
  },
  {
    id: VehicleTypeId.Limousine,
    key: VehicleTypeKey.Limousine,
    label: 'Limousine',
  },
  {
    id: VehicleTypeId.Trolley,
    key: VehicleTypeKey.Trolley,
    label: 'Trolley',
  },
  {
    id: VehicleTypeId.SchoolBus,
    key: VehicleTypeKey.SchoolBus,
    label: 'School Bus',
  },
  {
    id: VehicleTypeId.Van,
    key: VehicleTypeKey.Van,
    label: 'Van',
  },
]

/**
 * Converts a provided `VehicleTypeKey` to its corresponding `VehicleTypeLabel`.
 * If the key does not match any predefined mapping, an error is thrown.
 *
 * @param {VehicleTypeKey} key - The vehicle type key to convert.
 * @returns {VehicleTypeLabel} The corresponding vehicle type label.
 * @throws {Error} If the key does not have a corresponding label in the map.
 */
export const vehicleTypeKeyToLabel = (key: VehicleTypeKey): VehicleTypeLabel => {
  const map: { [key in VehicleTypeKey]: VehicleTypeLabel } = {
    [VehicleTypeKey.CharterBus]: VehicleTypeLabel.CharterBus,
    [VehicleTypeKey.MiniBus]: VehicleTypeLabel.MiniBus,
    [VehicleTypeKey.Sprinter]: VehicleTypeLabel.Sprinter,
    [VehicleTypeKey.PartyBus]: VehicleTypeLabel.PartyBus,
    [VehicleTypeKey.Sedan]: VehicleTypeLabel.Sedan,
    [VehicleTypeKey.SUV]: VehicleTypeLabel.SUV,
    [VehicleTypeKey.Limousine]: VehicleTypeLabel.Limousine,
    [VehicleTypeKey.Trolley]: VehicleTypeLabel.Trolley,
    [VehicleTypeKey.SchoolBus]: VehicleTypeLabel.SchoolBus,
    [VehicleTypeKey.Van]: VehicleTypeLabel.Van,
  }

  if (!(key in map)) {
    throw new Error(`Invalid VehicleTypeKey: ${key}`)
  }

  return map[key]
}

export const getVehicleTypeLabelByKey = (searchKey: string): string => {
  return VEHICLE_TYPES.find(({ key }) => key === searchKey)?.label || ''
}

/**
 * Narrows a list of assignable vehicles to those of a vehicle type.
 *
 * @param vehicles - the assignable vehicles to filter.
 * @param vehicleTypeKey - the vehicle type key to match on.
 * @returns the vehicles of the given type.
 */
export const filterAssignableVehiclesByTypeKey = (
  vehicles: AssignableVehicle[],
  vehicleTypeKey: VehicleTypeKey
): AssignableVehicle[] =>
  vehicles.filter((vehicle) => vehicle.type === vehicleTypeKey)

/**
 * Resolves a vehicle's eligibility status. A vehicle the endpoint sent no eligibility for is
 * eligible.
 *
 * @param eligibility - a vehicle's eligibility as the endpoint returns it.
 * @returns the vehicle's eligibility status.
 */
export const toVehicleEligibilityStatus = (
  eligibility: AssignableVehicleEligibilityResponse | null | undefined
): VehicleEligibilityStatus => {
  let status = VehicleEligibilityStatus.Eligible

  const {
    updatedAfterCompletenessThreshold,
    openAmenityReviewsSummary,
    matchesAmenities,
  } = eligibility ?? {}
  if (updatedAfterCompletenessThreshold === false) {
    status = VehicleEligibilityStatus.ProfileIncomplete
  } else if (
    openAmenityReviewsSummary === VehicleAmenityReviewStatus.AwaitingEvidence
  ) {
    status = VehicleEligibilityStatus.NeedsOperatorAttention
  } else if (
    openAmenityReviewsSummary === VehicleAmenityReviewStatus.AwaitingDecision
  ) {
    status = VehicleEligibilityStatus.PendingApproval
  } else if (matchesAmenities === false) {
    status = VehicleEligibilityStatus.DoesNotMeetTripRequirements
  }

  return status
}

/**
 * Converts an assignable vehicle out of the endpoint's field names.
 *
 * @param response - an assignable vehicle as the endpoint returns it.
 * @returns the assignable vehicle.
 */
export const toAssignableVehicle = (
  response: AssignableVehicleResponse
): AssignableVehicle => ({
  id: response.vehicleId,
  name: response.vehicleName,
  type: response.vehicleType,
  vinVerificationStatus: response.vinVerificationStatus,
  eligibility: toVehicleEligibilityStatus(response.eligibility),
})
